import { Button } from '@deepguide-ai/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@deepguide-ai/ui/dialog';
import { Input } from '@deepguide-ai/ui/input';
import { Label } from '@deepguide-ai/ui/label';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { FC, useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { ConfigModalProps } from '../types';
import { IntegrationFeature, IntegrationProvider } from '../types';

const VantaIconWrapper: FC = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="24" height="24" rx="4" fill="#5B4FFF"/>
    <path
      d="M12 6L16 10L12 14L8 10L12 6Z"
      fill="white"
    />
    <path
      d="M12 10L16 14L12 18L8 14L12 10Z"
      fill="white"
      opacity="0.6"
    />
  </svg>
);

/**
 * RFC-085: Vanta configuration modal
 * Allows users to configure API credentials for Vanta compliance automation
 */
const VantaConfigModal: FC<ConfigModalProps> = ({
  open,
  onOpenChange,
  integration,
  onComplete,
  workspaceId,
}) => {
  const [apiKey, setApiKey] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://api.vanta.com/v1');

  useEffect(() => {
    if (integration && open) {
      const config = integration.config as any;
      setApiKey(config?.apiKey || '');
      setOrganizationId(config?.organizationId || '');
      setBaseUrl(config?.baseUrl || 'https://api.vanta.com/v1');
    }
  }, [integration, open]);

  const [isSaving, setIsSaving] = useState(false);

  const onSave = async () => {
    if (!apiKey.trim()) {
      toast.error('API Key is required');
      return;
    }

    setIsSaving(true);
    try {
      const config = {
        apiKey: apiKey.trim(),
        organizationId: organizationId.trim(),
        baseUrl: baseUrl.trim(),
      };

      if (integration) {
        // Update existing integration metadata (config is stored separately)
        await trpcClient.integrations.updateIntegration.mutate({
          id: integration.id,
          metadata: {
            ...integration.metadata,
            vantaConfig: config,
          },
        });
        toast.success('Vanta integration updated successfully');
      }

      queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
      onOpenChange(false);
      onComplete();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save Vanta integration');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Configure Vanta Integration</DialogTitle>
          <DialogDescription>
            Connect your Vanta account to automatically upload compliance evidence packs.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="apiKey">
              API Key <span className="text-destructive">*</span>
            </Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="Enter your Vanta API key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              You can find your API key in Vanta Settings → Integrations → API Tokens
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="organizationId">Organization ID (Optional)</Label>
            <Input
              id="organizationId"
              placeholder="Enter your Vanta organization ID"
              value={organizationId}
              onChange={(e) => setOrganizationId(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="baseUrl">API Base URL</Label>
            <Input
              id="baseUrl"
              placeholder="https://api.vanta.com/v1"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Default: https://api.vanta.com/v1
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Vanta Integration Provider
 * Manages Vanta compliance automation integration
 */
export const VantaProvider: IntegrationProvider = {
  id: 'Vanta',
  name: 'Vanta',
  description: 'Automated compliance evidence uploads to Vanta platform',
  Icon: VantaIconWrapper,
  ConfigModal: VantaConfigModal,

  getFeatures: (): IntegrationFeature[] => [
    {
      id: 'compliance_upload',
      name: 'Compliance Upload',
      description: 'Automatically upload SOC 2 evidence packs to Vanta',
      enabled: true,
    },
    {
      id: 'scheduled_evidence',
      name: 'Scheduled Evidence Generation',
      description: 'Generate and upload evidence on a schedule',
      enabled: true,
    },
  ],

  handleConnect: async ({ workspaceId, onSuccess }) => {
    // Vanta uses API key authentication - create integration record first
    // Configuration is done through the modal
    try {
      const integration = await trpcClient.integrations.create.mutate({
        workspaceId,
        type: 'Vanta',
        name: 'Vanta',
      });

      // Call onSuccess to trigger modal opening
      onSuccess?.(integration);

      return { success: true };
    } catch (error) {
      console.error('Failed to create Vanta integration:', error);
      return { success: false, error };
    }
  },

  syncData: async (_integration) => {
    // No data sync needed for Vanta - it's push-only (we upload to them)
    console.log('Vanta integration does not require data sync (push-only)');
  },
};
